/**
 * Public Guest Results Page — không cần đăng nhập.
 *
 * Theo R1→R2 transition doc Bước 5:
 *   "Báo cáo công khai chỉ liệt kê 4 đội đi tiếp theo thứ tự TÊN ĐỘI
 *    (không theo thứ hạng) để giảm áp lực so sánh"
 */
import { useParams, Link as RouterLink } from 'react-router-dom'
import {
  Box,
  Container,
  Typography,
  Paper,
  Stack,
  Chip,
  Alert,
  CircularProgress,
  Divider,
  Button,
  Grid,
} from '@mui/material'
import { CheckCircle, EmojiEvents, ArrowBack, AccountBalance } from '@mui/icons-material'
import {
  useGetPublicResultsCasesQuery,
  useGetPublicLatestResultsQuery,
  useGetPublicVerdictsQuery,
} from '../store/api/reportsApi'
import { Gavel } from '@mui/icons-material'

const NAVY = '#144E8C'
const PINK = '#F64183'
const GOLD = '#C9A24E'

function CaseListLanding() {
  const { data, isLoading, error } = useGetPublicResultsCasesQuery()

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, sm: 6 } }}>
      <Stack spacing={3} alignItems="center" mb={4}>
        <EmojiEvents sx={{ fontSize: 56, color: GOLD }} />
        <Typography variant="h4" fontWeight={700} align="center" sx={{ color: NAVY }}>
          Kết quả Cuộc thi Phiên toà Giả định
        </Typography>
        <Typography variant="body1" color="text.secondary" align="center">
          UEL E-Court Platform · Trang công bố kết quả công khai
        </Typography>
      </Stack>

      {isLoading && (
        <Box sx={{ textAlign: 'center', py: 6 }}>
          <CircularProgress />
        </Box>
      )}
      {error && (
        <Alert severity="warning">Hiện chưa có kết quả công bố nào.</Alert>
      )}
      {data && data.length === 0 && (
        <Alert severity="info">Hiện chưa có cuộc thi nào được công bố kết quả.</Alert>
      )}

      <Grid container spacing={2}>
        {data?.map((c) => (
          <Grid item xs={12} sm={6} key={c.case_id}>
            <Paper
              elevation={0}
              sx={{
                p: 3,
                border: '1px solid',
                borderColor: 'divider',
                borderRadius: 2,
                transition: 'all 0.2s',
                '&:hover': {
                  borderColor: NAVY,
                  boxShadow: 3,
                  transform: 'translateY(-2px)',
                },
              }}
            >
              <Stack direction="row" spacing={2} alignItems="flex-start">
                <AccountBalance sx={{ color: NAVY, fontSize: 32 }} />
                <Box flex={1}>
                  <Typography variant="h6" fontWeight={600} gutterBottom>
                    {c.title}
                  </Typography>
                  <Button
                    component={RouterLink}
                    to={`/public/results/${c.case_id}`}
                    variant="contained"
                    size="small"
                    sx={{ bgcolor: NAVY, '&:hover': { bgcolor: '#0E3970' } }}
                  >
                    Xem kết quả →
                  </Button>
                </Box>
              </Stack>
            </Paper>
          </Grid>
        ))}
      </Grid>
    </Container>
  )
}

function CaseResultsView({ caseId }: { caseId: string }) {
  const { data, isLoading, error } = useGetPublicLatestResultsQuery(caseId)
  const { data: verdicts = [] } = useGetPublicVerdictsQuery(caseId)

  if (isLoading) {
    return (
      <Container maxWidth="md" sx={{ py: 6, textAlign: 'center' }}>
        <CircularProgress />
      </Container>
    )
  }
  if (error || !data) {
    return (
      <Container maxWidth="md" sx={{ py: 6 }}>
        <Alert severity="warning">Chưa có kết quả công bố cho cuộc thi này.</Alert>
        <Button
          component={RouterLink}
          to="/public/results"
          startIcon={<ArrowBack />}
          sx={{ mt: 2 }}
        >
          Quay lại
        </Button>
      </Container>
    )
  }

  const publishedDate = data.published_at
    ? new Date(data.published_at).toLocaleDateString('vi-VN')
    : '—'

  return (
    <Container maxWidth="md" sx={{ py: { xs: 3, sm: 6 } }}>
      <Button
        component={RouterLink}
        to="/public/results"
        startIcon={<ArrowBack />}
        sx={{ mb: 2 }}
      >
        Tất cả cuộc thi
      </Button>

      {/* Hero header */}
      <Paper
        elevation={0}
        sx={{
          background: `linear-gradient(135deg, ${NAVY} 0%, ${PINK} 100%)`,
          color: 'white',
          p: { xs: 3, sm: 5 },
          borderRadius: 2,
          mb: 4,
          textAlign: 'center',
        }}
      >
        <EmojiEvents sx={{ fontSize: 64, color: GOLD, mb: 2 }} />
        <Typography variant="overline" sx={{ opacity: 0.9, letterSpacing: 2 }}>
          Báo cáo công khai
        </Typography>
        <Typography variant="h4" fontWeight={700} gutterBottom>
          {data.case_title}
        </Typography>
        {data.round && (
          <Chip
            label={data.round.round_name}
            sx={{ bgcolor: 'rgba(255,255,255,0.2)', color: 'white', fontWeight: 600 }}
          />
        )}
        <Typography variant="body2" sx={{ mt: 2, opacity: 0.9 }}>
          Công bố ngày {publishedDate}
        </Typography>
      </Paper>

      {/* Stats */}
      <Grid container spacing={2} mb={4}>
        <Grid item xs={6}>
          <Paper sx={{ p: 2, textAlign: 'center', borderTop: `4px solid ${NAVY}` }}>
            <Typography variant="h3" fontWeight={700} sx={{ color: NAVY }}>
              {data.total_teams}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Tổng số đội tham gia
            </Typography>
          </Paper>
        </Grid>
        <Grid item xs={6}>
          <Paper sx={{ p: 2, textAlign: 'center', borderTop: `4px solid ${GOLD}` }}>
            <Typography variant="h3" fontWeight={700} sx={{ color: GOLD }}>
              {data.advancing_count}
            </Typography>
            <Typography variant="caption" color="text.secondary">
              Đội vào vòng tiếp theo
            </Typography>
          </Paper>
        </Grid>
      </Grid>

      {/* Advancing teams — sorted by NAME, NOT by rank */}
      <Paper elevation={0} sx={{ p: { xs: 2, sm: 4 }, border: '1px solid', borderColor: 'divider', borderRadius: 2 }}>
        <Stack direction="row" spacing={1} alignItems="center" mb={2}>
          <CheckCircle sx={{ color: GOLD }} />
          <Typography variant="h5" fontWeight={700} sx={{ color: NAVY }}>
            Danh sách đội vào vòng tiếp theo
          </Typography>
        </Stack>
        <Typography variant="caption" color="text.secondary" display="block" mb={3}>
          (Thứ tự công bố theo tên đội, không theo thứ hạng — Điều 10 Thể lệ)
        </Typography>
        <Divider sx={{ mb: 2 }} />

        <Stack spacing={1.5}>
          {data.advancing_teams.map((team, idx) => (
            <Paper
              key={`${team.team_name}_${idx}`}
              elevation={0}
              sx={{
                p: 2,
                bgcolor: 'background.default',
                borderLeft: `4px solid ${GOLD}`,
                display: 'flex',
                alignItems: 'center',
                gap: 2,
              }}
            >
              <CheckCircle sx={{ color: GOLD }} />
              <Box flex={1}>
                <Typography variant="h6" fontWeight={600}>
                  {team.team_name}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {team.university}
                </Typography>
              </Box>
            </Paper>
          ))}
        </Stack>

        {data.note && (
          <Alert severity="info" sx={{ mt: 3 }}>
            {data.note}
          </Alert>
        )}
      </Paper>

      {/* R3 — Bản án Chung kết đã công bố */}
      {verdicts.length > 0 && (
        <Paper
          elevation={0}
          sx={{
            mt: 4,
            p: { xs: 2, sm: 4 },
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: 2,
            background: `linear-gradient(135deg, ${NAVY}08 0%, transparent 100%)`,
          }}
        >
          <Stack direction="row" spacing={1} alignItems="center" mb={3}>
            <Gavel sx={{ color: NAVY }} />
            <Typography variant="h5" fontWeight={700} sx={{ color: NAVY }}>
              Bản án đã công bố
            </Typography>
          </Stack>

          <Stack spacing={2}>
            {verdicts.map((v) => (
              <Paper
                key={v.courtroom_id}
                elevation={0}
                sx={{
                  p: 3,
                  bgcolor: 'background.paper',
                  borderLeft: `4px solid ${NAVY}`,
                }}
              >
                <Stack direction="row" spacing={1} alignItems="center" mb={1} flexWrap="wrap">
                  {v.round_name && (
                    <Chip
                      size="small"
                      label={v.round_name}
                      sx={{ bgcolor: NAVY, color: 'white' }}
                    />
                  )}
                  {v.winner_team && (
                    <Chip
                      size="small"
                      icon={<EmojiEvents sx={{ color: `${GOLD} !important` }} />}
                      label={`Đội thắng: ${v.winner_team.team_name}`}
                      sx={{ borderColor: GOLD, color: GOLD }}
                      variant="outlined"
                    />
                  )}
                  {v.published_at && (
                    <Typography variant="caption" color="text.secondary">
                      Công bố {new Date(v.published_at).toLocaleDateString('vi-VN')}
                    </Typography>
                  )}
                </Stack>
                {v.verdict_summary && (
                  <Typography variant="body1" fontWeight={500} mb={1}>
                    {v.verdict_summary}
                  </Typography>
                )}
                {v.verdict_legal_basis && (
                  <Box mt={1.5}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Căn cứ pháp lý:
                    </Typography>
                    <Typography variant="body2">{v.verdict_legal_basis}</Typography>
                  </Box>
                )}
                {v.verdict_penalty && (
                  <Box mt={1.5}>
                    <Typography variant="caption" color="text.secondary" display="block">
                      Hình phạt / yêu cầu:
                    </Typography>
                    <Typography variant="body2">{v.verdict_penalty}</Typography>
                  </Box>
                )}
              </Paper>
            ))}
          </Stack>
        </Paper>
      )}

      <Typography variant="caption" display="block" align="center" color="text.secondary" sx={{ mt: 4 }}>
        © UEL E-Court Platform — Cuộc thi Phiên toà Giả định
      </Typography>
    </Container>
  )
}

export default function PublicResultsPage() {
  const { caseId } = useParams<{ caseId?: string }>()
  if (caseId) {
    return <CaseResultsView caseId={caseId} />
  }
  return <CaseListLanding />
}
